/**
 * Wire → domain projection for the admin API: snake_case shapes from `backend-types/admin-api.ts`
 * into the camelCase models in `domain/admin.ts`. Called only from `store/slices/adminThunks.ts`,
 * at the API boundary, as every other endpoint in this app is projected.
 *
 * These are renames, not repairs. There is deliberately **no numeric coercion** here: PostgREST
 * returns JSON numbers, and `transcription_seconds_total` is rounded to a whole number in the
 * `admin_user_overview` view so it agrees with `usage.seconds_used` on the detail endpoint. An
 * earlier version of this file ran everything through `Number()`; that guard is gone because the
 * thing it guarded against does not happen, and a coercion that also turns null into 0 is worse
 * than no coercion — "unknown" and "zero" are different answers on every screen in this panel.
 *
 * The `?? null` on optional fields is not coercion. Several wire types mark a field optional (`?`)
 * where the domain wants an explicit `null`, and the two detail sub-shapes are `Pick`s that omit
 * fields the full row has.
 */

import type {
  AdminConversionListResponse,
  AdminConversionRow,
  AdminEmailListResponse,
  AdminEmailLogRow,
  AdminPendingSignup,
  AdminSetupEmailSummary,
  AdminSignupListResponse,
  AdminStats,
  AdminSuppression as AdminSuppressionWire,
  AdminSuppressionListResponse,
  AdminTranscriptionListResponse,
  AdminTranscriptionRow,
  AdminUserDetailResponse,
  AdminUserListResponse,
  AdminUserRow,
} from '../backend-types/admin-api';
import type {
  AdminConversion,
  AdminDashboardStats,
  AdminEmail,
  AdminPage,
  AdminSetupEmail,
  AdminSignup,
  AdminSuppression,
  AdminTranscription,
  AdminUser,
  AdminUserProfile,
  AdminUserRef,
} from './admin';

/* ==========================================================================
 * Dashboard
 * ========================================================================== */

export const toAdminStats = (wire: AdminStats): AdminDashboardStats => ({
  usersTotal: wire.users_total,
  subscribersActive: wire.subscribers_active,
  subscribersPaid: wire.subscribers_paid,
  usersNew30d: wire.users_new_30d,
  transcriptionsInFlight: wire.transcriptions_in_flight,
  signupsOpen: wire.signups_open,
  signupsFailed: wire.signups_failed,
  emailsUndelivered30d: wire.emails_undelivered_30d,
  suppressionsActive: wire.suppressions_active,
});

/* ==========================================================================
 * Users
 * ========================================================================== */

const toUserRef = (
  wire: {id: string; email: string; name: string | null} | null | undefined
): AdminUserRef | null => (wire ? {id: wire.id, email: wire.email, name: wire.name} : null);

export const toAdminUser = (wire: AdminUserRow): AdminUser => ({
  id: wire.id,
  supabaseUserId: wire.supabase_user_id,
  email: wire.email,
  name: wire.name,
  avatarUrl: wire.avatar_url,
  isAdmin: wire.is_admin,
  passwordSet: wire.password_set,
  welcomePage: wire.welcome_page,

  subscribed: wire.subscribed,
  subscriptionPlan: wire.subscription_plan,
  subscriptionExpiresAt: wire.subscription_expires_at,

  isSubscriptionActive: wire.is_subscription_active,
  effectiveSubscriptionPlan: wire.effective_subscription_plan,

  paddleCustomerId: wire.paddle_customer_id,
  paddleSubscriptionId: wire.paddle_subscription_id,
  lastSyncedAt: wire.last_synced_at,
  createdAt: wire.created_at,
  updatedAt: wire.updated_at,

  transcriptionCount: wire.transcription_count,
  transcriptionSecondsTotal: wire.transcription_seconds_total,
  transcriptionsInFlight: wire.transcriptions_in_flight,
  transcriptionsFailed: wire.transcriptions_failed,
  lastTranscriptionAt: wire.last_transcription_at,
  conversionCount: wire.conversion_count,
  lastConversionAt: wire.last_conversion_at,
  projectCount: wire.project_count,
  audioUploadCount: wire.audio_upload_count,
});

export const toAdminUserPage = (wire: AdminUserListResponse): AdminPage<AdminUser> => ({
  items: (wire.users ?? []).map(toAdminUser),
  total: wire.total,
  limit: wire.limit,
  offset: wire.offset,
});

/* ==========================================================================
 * Signups
 * ========================================================================== */

export const toAdminSignup = (wire: AdminPendingSignup): AdminSignup => ({
  id: wire.id,
  email: wire.email,
  name: wire.name ?? null,
  status: wire.status,
  paddleCustomerId: wire.paddle_customer_id ?? null,
  paddleSubscriptionId: wire.paddle_subscription_id ?? null,
  userId: wire.user_id,
  lastError: wire.last_error ?? null,
  loginLinkSentAt: wire.login_link_sent_at,
  provisionedAt: wire.provisioned_at,
  expiresAt: wire.expires_at,
  createdAt: wire.created_at,
});

export const toAdminSignupPage = (wire: AdminSignupListResponse): AdminPage<AdminSignup> => ({
  items: (wire.signups ?? []).map(toAdminSignup),
  total: wire.total,
  limit: wire.limit,
  offset: wire.offset,
});

/* ==========================================================================
 * Email
 * ========================================================================== */

export const toAdminEmail = (wire: AdminEmailLogRow): AdminEmail => ({
  id: wire.id,
  emailType: wire.email_type,
  recipient: wire.recipient,
  status: wire.status,
  error: wire.error,
  triggeredBy: wire.triggered_by,
  triggeredByUserId: wire.triggered_by_user_id ?? null,
  providerMessageId: wire.provider_message_id,
  providerEventAt: wire.provider_event_at,
  createdAt: wire.created_at,
  userId: wire.user_id ?? null,
  user: toUserRef(wire.user),
});

export const toAdminEmailPage = (wire: AdminEmailListResponse): AdminPage<AdminEmail> => ({
  items: (wire.emails ?? []).map(toAdminEmail),
  total: wire.total,
  limit: wire.limit,
  offset: wire.offset,
});

export const toAdminSetupEmail = (wire: AdminSetupEmailSummary): AdminSetupEmail => ({
  status: wire.status,
  sentAt: wire.sent_at,
  deliveredAt: wire.delivered_at,
  error: wire.error,
  triggeredBy: wire.triggered_by,
  sendCount: wire.send_count,
});

export const toAdminSuppression = (wire: AdminSuppressionWire): AdminSuppression => ({
  id: wire.id ?? null,
  email: wire.email,
  origin: wire.origin,
  sourceEmailId: wire.source_email_id,
  suppressedAt: wire.suppressed_at,
  removedAt: wire.removed_at ?? null,
  createdAt: wire.created_at ?? null,
});

export const toAdminSuppressionPage = (
  wire: AdminSuppressionListResponse
): AdminPage<AdminSuppression> => ({
  items: (wire.suppressions ?? []).map(toAdminSuppression),
  total: wire.total,
  limit: wire.limit,
  offset: wire.offset,
});

/* ==========================================================================
 * Activity
 * ========================================================================== */

/**
 * Handles both the full row and the trimmed `Pick` embedded in the user detail — the detail's
 * version has no `size`, `error`, `project_id` or `assemblyai_transcript_id`, so those read through
 * as null rather than the two shapes needing two mappers and drifting apart.
 */
export const toAdminTranscription = (
  wire: AdminTranscriptionRow | AdminUserDetailResponse['recent_transcriptions'][number]
): AdminTranscription => ({
  id: wire.id,
  name: wire.name,
  audioFilename: wire.audio_filename,
  status: wire.status,
  durationSeconds: wire.duration_seconds,
  size: (wire as AdminTranscriptionRow).size ?? null,
  isDeleted: wire.is_deleted,
  error: (wire as AdminTranscriptionRow).error ?? null,
  projectId: (wire as AdminTranscriptionRow).project_id ?? null,
  assemblyaiTranscriptId: (wire as AdminTranscriptionRow).assemblyai_transcript_id ?? null,
  createdAt: wire.created_at,
  updatedAt: (wire as AdminTranscriptionRow).updated_at ?? null,
  userId: (wire as AdminTranscriptionRow).user_id ?? null,
  user: toUserRef((wire as AdminTranscriptionRow).user),
});

export const toAdminTranscriptionPage = (
  wire: AdminTranscriptionListResponse
): AdminPage<AdminTranscription> => ({
  items: (wire.transcriptions ?? []).map(toAdminTranscription),
  total: wire.total,
  limit: wire.limit,
  offset: wire.offset,
});

/** Same two-shapes-one-mapper arrangement as the transcriptions above. */
export const toAdminConversion = (
  wire: AdminConversionRow | AdminUserDetailResponse['recent_conversions'][number]
): AdminConversion => ({
  id: wire.id,
  transcriptionId: wire.transcription_id,
  projectId: (wire as AdminConversionRow).project_id ?? null,
  mode: wire.mode,
  markers: wire.markers,
  censoredWords: wire.censored_words,
  totalCuts: wire.total_cuts,
  wordListName: (wire as AdminConversionRow).word_list_name ?? null,
  csvR2Key: (wire as AdminConversionRow).csv_r2_key ?? null,
  xmlR2Key: (wire as AdminConversionRow).xml_r2_key ?? null,
  audioPath: wire.audio_path ?? null,
  beepSoundPath: wire.beep_sound_path ?? null,
  videoEditor: wire.video_editor ?? null,
  fps: wire.fps ?? null,
  createdAt: wire.created_at,
});

export const toAdminConversionPage = (
  wire: AdminConversionListResponse
): AdminPage<AdminConversion> => ({
  items: (wire.conversions ?? []).map(toAdminConversion),
  total: wire.total,
  limit: wire.limit,
  offset: wire.offset,
});

/* ==========================================================================
 * User detail
 * ========================================================================== */

export const toAdminUserProfile = (wire: AdminUserDetailResponse): AdminUserProfile => ({
  user: {
    id: wire.user.id,
    supabaseUserId: wire.user.supabase_user_id,
    email: wire.user.email,
    name: wire.user.name,
    avatarUrl: wire.user.avatar_url,
    isAdmin: wire.user.is_admin,
    passwordSet: wire.user.password_set,
    welcomePage: wire.user.welcome_page,
    createdAt: wire.user.created_at,
    updatedAt: wire.user.updated_at,
  },
  subscription: {
    subscribed: wire.subscription.subscribed,
    subscriptionPlan: wire.subscription.subscription_plan,
    subscriptionExpiresAt: wire.subscription.subscription_expires_at,
    isSubscriptionActive: wire.subscription.is_subscription_active,
    effectiveSubscriptionPlan: wire.subscription.effective_subscription_plan,
    paddleCustomerId: wire.subscription.paddle_customer_id,
    paddleSubscriptionId: wire.subscription.paddle_subscription_id,
    lastSyncedAt: wire.subscription.last_synced_at,
  },
  // Null all the way through rather than a zeroed object: the screen has to say "unavailable".
  usage: wire.usage
    ? {
      plan: wire.usage.plan,
      secondsUsed: wire.usage.seconds_used,
      secondsLimit: wire.usage.seconds_limit,
      secondsRemaining: wire.usage.seconds_remaining,
      periodStart: wire.usage.period_start,
      periodEnd: wire.usage.period_end,
      maxFileSeconds: wire.usage.max_file_seconds,
    }
    : null,
  lifetime: {
    transcriptionCount: wire.lifetime.transcription_count,
    transcriptionSecondsTotal: wire.lifetime.transcription_seconds_total,
    transcriptionsInFlight: wire.lifetime.transcriptions_in_flight,
    transcriptionsFailed: wire.lifetime.transcriptions_failed,
    conversionCount: wire.lifetime.conversion_count,
    audioUploadCount: wire.lifetime.audio_upload_count,
    projectCount: wire.lifetime.project_count,
    lastTranscriptionAt: wire.lifetime.last_transcription_at,
    lastConversionAt: wire.lifetime.last_conversion_at,
  },
  signup: wire.signup ? toAdminSignup(wire.signup) : null,
  setupEmail: wire.setup_email ? toAdminSetupEmail(wire.setup_email) : null,
  emailSuppression: wire.email_suppression ? toAdminSuppression(wire.email_suppression) : null,
  recentEmails: (wire.recent_emails ?? []).map(toAdminEmail),
  recentTranscriptions: (wire.recent_transcriptions ?? []).map(toAdminTranscription),
  recentConversions: (wire.recent_conversions ?? []).map(toAdminConversion),
  projects: (wire.projects ?? []).map(project => ({
    id: project.id,
    name: project.name,
    isDefault: project.is_default,
  })),
});
